import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { dataTablesApi, teamsApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table2,
  Plus,
  Search,
  Share2,
  ArrowLeft,
  RefreshCw,
  Lock,
  Users,
  Globe,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Eye,
  FileCode,
  Copy,
  Check,
  Pencil,
} from 'lucide-react';
import { toast } from 'sonner';
import { CreateDataTableDialog } from '@/components/dialogs/CreateDataTableDialog';
import { ShareDataTableDialog } from '@/components/dialogs/ShareDataTableDialog';
import { EditDataTableDialog } from '@/components/dialogs/EditDataTableDialog';
import { DataTableListView } from '@/components/data-tables/DataTableListView';
import { EditableDataTable } from '@/components/data-tables/EditableDataTable';
import type { DataTable, DataTableRow } from '@/types/data-tables';

interface Team {
  id: string;
  name: string;
}

// Format date for display
function formatDate(dateString: string) {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// Format cell value for display
function formatCellValue(value: any, type: string): string {
  if (value === null || value === undefined) return '-';
  if (type === 'json') return JSON.stringify(value);
  if (type === 'boolean') return value ? 'Yes' : 'No';
  if (type === 'date' || type === 'datetime') {
    const date = new Date(value);
    return type === 'datetime'
      ? date.toLocaleString()
      : date.toLocaleDateString();
  }
  return String(value);
}

export default function DataTables() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedTableId = searchParams.get('table');

  // State
  const [tables, setTables] = useState<DataTable[]>([]);
  const [loadingTables, setLoadingTables] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [teams, setTeams] = useState<Team[]>([]);

  // Selected table state
  const [selectedTable, setSelectedTable] = useState<DataTable | null>(null);
  const [tableRows, setTableRows] = useState<DataTableRow[]>([]);
  const [loadingRows, setLoadingRows] = useState(false);
  const [rowsPage, setRowsPage] = useState(1);
  const [rowsPageSize] = useState(50);
  const [totalRows, setTotalRows] = useState(0);

  // Dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [tableToShare, setTableToShare] = useState<DataTable | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tableToDelete, setTableToDelete] = useState<DataTable | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Preview state (for detail view)
  const [showPreview, setShowPreview] = useState(false);

  // Schema dialog state
  const [schemaDialogOpen, setSchemaDialogOpen] = useState(false);
  const [copiedSchema, setCopiedSchema] = useState(false);

  // Modal preview state (for list view quick preview)
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewTable, setPreviewTable] = useState<DataTable | null>(null);
  const [previewRows, setPreviewRows] = useState<DataTableRow[]>([]);
  const [loadingPreviewRows, setLoadingPreviewRows] = useState(false);

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [tableToEdit, setTableToEdit] = useState<DataTable | null>(null);

  // Load tables
  const loadTables = useCallback(async () => {
    try {
      setLoadingTables(true);
      const response = await dataTablesApi.getTables();
      setTables(response.data || []);
    } catch (error) {
      console.error('Failed to load data tables:', error);
      toast.error('Failed to load data tables');
    } finally {
      setLoadingTables(false);
    }
  }, []);

  // Load teams for sharing
  const loadTeams = useCallback(async () => {
    try {
      const response = await teamsApi.list();
      setTeams(response.data || []);
    } catch (error) {
      console.error('Failed to load teams:', error);
    }
  }, []);

  // Load table rows
  const loadRows = useCallback(async (tableId: string, page: number = 1) => {
    try {
      setLoadingRows(true);
      const response = await dataTablesApi.getRows(tableId, {
        page,
        pageSize: rowsPageSize,
      });
      setTableRows(response.data?.rows || []);
      setTotalRows(response.data?.total || 0);
    } catch (error) {
      console.error('Failed to load table rows:', error);
      toast.error('Failed to load table rows');
    } finally {
      setLoadingRows(false);
    }
  }, [rowsPageSize]);

  // Load table details
  const loadTableDetails = useCallback(async (tableId: string) => {
    try {
      const response = await dataTablesApi.getTable(tableId);
      setSelectedTable(response.data);
    } catch (error) {
      console.error('Failed to load table details:', error);
      toast.error('Failed to load table details');
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadTables();
    loadTeams();
  }, [loadTables, loadTeams]);

  // Load selected table details AND rows automatically
  useEffect(() => {
    if (selectedTableId) {
      loadTableDetails(selectedTableId);
      // Auto-load rows when navigating to detail view
      setShowPreview(true);
      setRowsPage(1);
      loadRows(selectedTableId, 1);
    } else {
      setSelectedTable(null);
      setTableRows([]);
      setShowPreview(false);
    }
  }, [selectedTableId, loadTableDetails, loadRows]);

  // Handle ?create=true query parameter to open create dialog
  useEffect(() => {
    if (searchParams.get('create') === 'true') {
      setCreateDialogOpen(true);
      // Remove the query parameter from URL
      searchParams.delete('create');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Handle back navigation
  const handleBack = () => {
    setSearchParams({});
  };

  // Handle delete
  const handleDelete = async () => {
    if (!tableToDelete) return;

    try {
      setDeleting(true);
      await dataTablesApi.deleteTable(tableToDelete.id);
      toast.success('Data table deleted successfully');
      setDeleteDialogOpen(false);
      setTableToDelete(null);

      // If we deleted the selected table, go back
      if (selectedTableId === tableToDelete.id) {
        handleBack();
      }

      loadTables();
    } catch (error: any) {
      console.error('Failed to delete table:', error);
      toast.error(error.response?.data?.message || 'Failed to delete table');
    } finally {
      setDeleting(false);
    }
  };

  // Handle share
  const handleShare = (table: DataTable) => {
    setTableToShare(table);
    setShareDialogOpen(true);
  };

  // Handle view data from list (modal preview)
  const handleViewData = async (table: DataTable) => {
    setPreviewTable(table);
    setPreviewModalOpen(true);
    setLoadingPreviewRows(true);
    setPreviewRows([]);

    try {
      const response = await dataTablesApi.getRows(table.id, {
        page: 1,
        pageSize: 50,
      });
      setPreviewRows(response.data?.rows || []);
    } catch (error) {
      console.error('Failed to load preview rows:', error);
      toast.error('Failed to load table data');
    } finally {
      setLoadingPreviewRows(false);
    }
  };

  // Handle view schema from list (modal)
  const handleViewSchema = (table: DataTable) => {
    setPreviewTable(table);
    setSchemaDialogOpen(true);
  };

  // Handle edit table
  const handleEditTable = (table: DataTable) => {
    setTableToEdit(table);
    setEditDialogOpen(true);
  };

  // Get visibility icon
  const getVisibilityIcon = (visibility: string) => {
    switch (visibility) {
      case 'public':
        return <Globe className="w-4 h-4" />;
      case 'team':
        return <Users className="w-4 h-4" />;
      default:
        return <Lock className="w-4 h-4" />;
    }
  };

  // Pagination
  const totalPages = Math.ceil(totalRows / rowsPageSize);
  const handlePrevPage = () => {
    if (rowsPage > 1 && selectedTableId) {
      const newPage = rowsPage - 1;
      setRowsPage(newPage);
      loadRows(selectedTableId, newPage);
    }
  };
  const handleNextPage = () => {
    if (rowsPage < totalPages && selectedTableId) {
      const newPage = rowsPage + 1;
      setRowsPage(newPage);
      loadRows(selectedTableId, newPage);
    }
  };

  // Handle preview button click
  const handleLoadPreview = () => {
    if (selectedTableId) {
      setShowPreview(true);
      loadRows(selectedTableId, 1);
    }
  };

  // Generate SQL schema format
  const generateSqlSchema = (table: DataTable): string => {
    const columnDefs = table.schema.columns.map((col) => {
      let sqlType = 'TEXT';
      switch (col.type.toLowerCase()) {
        case 'number':
        case 'integer':
          sqlType = 'INTEGER';
          break;
        case 'decimal':
        case 'float':
          sqlType = 'DECIMAL';
          break;
        case 'boolean':
          sqlType = 'BOOLEAN';
          break;
        case 'datetime':
          sqlType = 'DATETIME';
          break;
        case 'date':
          sqlType = 'DATE';
          break;
        case 'json':
        case 'object':
        case 'array':
          sqlType = 'JSON';
          break;
        default:
          sqlType = 'TEXT';
      }
      const nullable = col.required ? ' NOT NULL' : '';
      return `  ${col.name} ${sqlType}${nullable}`;
    });

    const primaryKey = table.schema.primaryKey
      ? `,\n  PRIMARY KEY (${table.schema.primaryKey})`
      : '';

    return `CREATE TABLE ${table.name} (\n  id TEXT NOT NULL,\n${columnDefs.join(',\n')}${primaryKey}\n);`;
  };

  // Generate JSON schema format
  const generateJsonSchema = (table: DataTable): string => {
    return JSON.stringify(table.schema, null, 2);
  };

  // Copy schema to clipboard
  const handleCopySchema = async (content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedSchema(true);
      toast.success('Schema copied to clipboard');
      setTimeout(() => setCopiedSchema(false), 2000);
    } catch (error) {
      toast.error('Failed to copy schema');
    }
  };

  // Render table list view (collections pattern)
  const renderTableList = () => (
    <div className="h-full flex flex-col overflow-hidden p-4 md:p-6">
      {/* Header */}
      <div className="flex-none mb-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-2">
              <Table2 className="w-6 h-6" />
              Data Tables
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Store and query structured data without external database setup
            </p>
          </div>
        </div>
      </div>

      {/* Header row with search and actions */}
      <div className="flex-none flex items-center justify-between mb-4 gap-4">
        <div className="relative w-[250px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search tables..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button onClick={() => setCreateDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          New Table
        </Button>
      </div>

      {/* Tables List */}
      <div className="flex-1 overflow-hidden">
        <DataTableListView
          tables={tables}
          loading={loadingTables}
          searchQuery={searchQuery}
          onTableClick={handleViewData}
          onShareTable={handleShare}
          onViewSchema={handleViewSchema}
          onEditTable={handleEditTable}
          onDeleteTable={(table) => {
            setTableToDelete(table);
            setDeleteDialogOpen(true);
          }}
          onCreateTable={() => setCreateDialogOpen(true)}
          emptyStateTitle="No data tables yet"
          emptyStateDescription="Create your first data table to get started"
        />
      </div>
    </div>
  );

  // Render table detail view (rows view)
  const renderTableDetail = () => {
    if (!selectedTable) {
      return (
        <div className="flex items-center justify-center h-full">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      );
    }

    return (
      <div className="h-full flex flex-col p-4 md:p-6">
        {/* Header */}
        <div className="flex-none mb-4">
          <div className="flex items-center gap-4 mb-2">
            <Button variant="ghost" size="icon" onClick={handleBack}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-semibold">{selectedTable.name}</h1>
                {getVisibilityIcon(selectedTable.visibility)}
              </div>
              {selectedTable.description && (
                <p className="text-sm text-muted-foreground mt-1">
                  {selectedTable.description}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSchemaDialogOpen(true)}
              >
                <FileCode className="w-4 h-4 mr-2" />
                View Schema
              </Button>
              {!showPreview ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleLoadPreview}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Preview Data
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (selectedTableId) {
                      loadRows(selectedTableId, rowsPage);
                    }
                  }}
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleShare(selectedTable)}
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
            </div>
          </div>

          {/* Stats */}
          <div className="flex items-center gap-6 text-sm text-muted-foreground ml-12">
            <span>{totalRows.toLocaleString()} rows</span>
            <span>{selectedTable.schema.columns.length} columns</span>
            <span>Updated {formatDate(selectedTable.updatedAt)}</span>
          </div>
        </div>

        {/* Rows Table - Editable */}
        <div className="flex-1 overflow-hidden border rounded-lg bg-card">
          {!showPreview ? (
            <div className="flex flex-col items-center justify-center h-full">
              <Eye className="w-12 h-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">Data Preview</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Click "Preview Data" to load and edit rows in this table.
              </p>
              <Button onClick={handleLoadPreview}>
                <Eye className="w-4 h-4 mr-2" />
                Preview Data
              </Button>
            </div>
          ) : loadingRows ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <EditableDataTable
              table={selectedTable}
              rows={tableRows}
              onRowsChange={() => {
                if (selectedTableId) {
                  loadRows(selectedTableId, rowsPage);
                  loadTableDetails(selectedTableId);
                }
              }}
              isOwner={selectedTable.isOwner !== false}
            />
          )}
        </div>

        {/* Pagination */}
        {showPreview && totalPages > 1 && (
          <div className="flex-shrink-0 flex items-center justify-between mt-2 px-3 py-1.5 bg-card border rounded-lg">
            <p className="text-sm text-muted-foreground">
              Showing up to 50 rows (Page {rowsPage} of {totalPages}, {totalRows.toLocaleString()} total rows)
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrevPage}
                disabled={rowsPage === 1}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleNextPage}
                disabled={rowsPage === totalPages}
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* Preview info when showing data */}
        {showPreview && tableRows.length > 0 && totalPages <= 1 && (
          <div className="flex-shrink-0 flex items-center mt-2 px-3 py-1.5 bg-card border rounded-lg">
            <p className="text-sm text-muted-foreground">
              Showing {Math.min(tableRows.length, 50)} of {totalRows.toLocaleString()} rows
            </p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="h-full">
      {selectedTableId ? renderTableDetail() : renderTableList()}

      {/* Create Dialog */}
      <CreateDataTableDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSuccess={loadTables}
        defaultVisibility="private"
      />

      {/* Share Dialog */}
      <ShareDataTableDialog
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
        table={tableToShare}
        teams={teams}
        onSuccess={() => {
          loadTables();
          if (selectedTableId) {
            loadTableDetails(selectedTableId);
          }
        }}
      />

      {/* Edit Dialog */}
      <EditDataTableDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        table={tableToEdit}
        onSuccess={() => {
          loadTables();
          if (selectedTableId) {
            loadTableDetails(selectedTableId);
          }
        }}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Data Table</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{tableToDelete?.name}"? This will permanently delete
              all {tableToDelete?.rowCount.toLocaleString()} rows. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleting}
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Schema Dialog */}
      <Dialog open={schemaDialogOpen} onOpenChange={(open) => {
        setSchemaDialogOpen(open);
        if (!open) setPreviewTable(null);
      }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Table Schema: {(previewTable || selectedTable)?.name}</DialogTitle>
            <DialogDescription>
              View the schema definition in SQL or JSON format
            </DialogDescription>
          </DialogHeader>
          {(previewTable || selectedTable) && (
            <Tabs defaultValue="sql" className="w-full flex-1 overflow-hidden flex flex-col">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="sql">SQL Format</TabsTrigger>
                <TabsTrigger value="json">JSON Format</TabsTrigger>
              </TabsList>
              <TabsContent value="sql" className="mt-4 flex-1 overflow-hidden">
                <div className="relative h-full">
                  <Button
                    variant="outline"
                    size="sm"
                    className="absolute top-2 right-2 z-10"
                    onClick={() => handleCopySchema(generateSqlSchema((previewTable || selectedTable)!))}
                  >
                    {copiedSchema ? (
                      <>
                        <Check className="w-4 h-4 mr-1" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mr-1" />
                        Copy
                      </>
                    )}
                  </Button>
                  <ScrollArea className="h-full max-h-[50vh] w-full rounded-md border bg-muted p-4">
                    <pre className="text-sm font-mono whitespace-pre-wrap">
                      {generateSqlSchema((previewTable || selectedTable)!)}
                    </pre>
                  </ScrollArea>
                </div>
              </TabsContent>
              <TabsContent value="json" className="mt-4 flex-1 overflow-hidden">
                <div className="relative h-full">
                  <Button
                    variant="outline"
                    size="sm"
                    className="absolute top-2 right-2 z-10"
                    onClick={() => handleCopySchema(generateJsonSchema((previewTable || selectedTable)!))}
                  >
                    {copiedSchema ? (
                      <>
                        <Check className="w-4 h-4 mr-1" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mr-1" />
                        Copy
                      </>
                    )}
                  </Button>
                  <ScrollArea className="h-full max-h-[50vh] w-full rounded-md border bg-muted p-4">
                    <pre className="text-sm font-mono whitespace-pre-wrap">
                      {generateJsonSchema((previewTable || selectedTable)!)}
                    </pre>
                  </ScrollArea>
                </div>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* Data Preview/Edit Modal (for list view) */}
      <Dialog open={previewModalOpen} onOpenChange={(open) => {
        setPreviewModalOpen(open);
        if (!open) {
          setPreviewTable(null);
          setPreviewRows([]);
        }
      }}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Table2 className="w-5 h-5" />
              {previewTable?.name}
            </DialogTitle>
            <DialogDescription>
              {previewTable?.rowCount.toLocaleString()} total rows • {previewTable?.schema.columns.length} columns
              {previewTable?.isOwner !== false && ' • Double-click cells to edit'}
            </DialogDescription>
          </DialogHeader>
          <div className="border rounded-lg bg-card overflow-hidden" style={{ height: 'calc(85vh - 150px)' }}>
            {loadingPreviewRows ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : previewTable ? (
              <EditableDataTable
                table={previewTable}
                rows={previewRows}
                onRowsChange={async () => {
                  // Refresh the preview rows
                  if (previewTable) {
                    try {
                      const response = await dataTablesApi.getRows(previewTable.id, {
                        page: 1,
                        pageSize: 50,
                      });
                      setPreviewRows(response.data?.rows || []);
                      // Also refresh the table to update row count
                      const tableResponse = await dataTablesApi.getTable(previewTable.id);
                      setPreviewTable(tableResponse.data);
                      // Refresh the main list
                      loadTables();
                    } catch (error) {
                      console.error('Failed to refresh preview:', error);
                    }
                  }
                }}
                isOwner={previewTable.isOwner !== false}
              />
            ) : null}
          </div>
          <div className="pt-2 text-sm text-muted-foreground">
            <span>Showing {Math.min(previewRows.length, 50)} of {previewTable?.rowCount.toLocaleString()} rows</span>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
